import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { ANLIEGEN, BRAND } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    telefon: "",
    anliegen: "",
    nachricht: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.anliegen || !form.nachricht) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Vielen Dank! Ich melde mich zeitnah bei Ihnen.");
      setForm({ name: "", email: "", telefon: "", anliegen: "", nachricht: "" });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.";
      toast.error(typeof msg === "string" ? msg : "Übermittlung fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-neutral-300 px-0 py-4 text-[#0A0A0A] placeholder:text-neutral-400 focus:outline-none focus:border-[#8A151B] transition-colors text-base font-body";

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="bg-white py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-10 bg-[#8A151B]" />
              <span className="text-xs uppercase tracking-[0.3em] font-bold text-[#8A151B]">
                Kontakt
              </span>
            </div>

            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] text-[#0A0A0A]">
              Reden wir
              <br />
              <span className="text-[#8A151B]">persönlich.</span>
            </h2>

            <p className="mt-8 text-lg text-neutral-600 leading-relaxed max-w-md">
              Schreiben Sie mir, was Sie brauchen — Verkauf, Reparatur,
              Pflege oder einfach ein Gespräch. Ich melde mich zeitnah
              persönlich zurück.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 border border-neutral-300 flex items-center justify-center group-hover:border-[#8A151B] group-hover:bg-[#8A151B] transition-all">
                  <Mail
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#0A0A0A] group-hover:text-white transition-colors"
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                    E-Mail
                  </p>
                  <p className="font-display text-base text-[#0A0A0A]">
                    {BRAND.email}
                  </p>
                </div>
              </a>

              <div className="flex items-center gap-4">
                <div className="w-12 h-12 border border-neutral-300 flex items-center justify-center">
                  <MapPin size={18} strokeWidth={1.5} className="text-[#0A0A0A]" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                    Region
                  </p>
                  <p className="font-display text-base text-[#0A0A0A]">
                    Südhessen
                  </p>
                </div>
              </div>

              <a
                href={`tel:${BRAND.phoneIntl}`}
                data-testid="contact-phone-link"
                className="flex items-center gap-4 group"
              >
                <div className="w-12 h-12 border border-neutral-300 flex items-center justify-center group-hover:border-[#8A151B] group-hover:bg-[#8A151B] transition-all">
                  <Phone
                    size={18}
                    strokeWidth={1.5}
                    className="text-[#0A0A0A] group-hover:text-white transition-colors"
                  />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">
                    Telefon
                  </p>
                  <p className="font-display text-base text-[#0A0A0A]">
                    {BRAND.phone}
                  </p>
                </div>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="border border-neutral-200 p-8 lg:p-12 bg-[#F5F5F5]"
            >
              {done ? (
                <div
                  data-testid="contact-success"
                  className="text-center py-16"
                >
                  <div className="inline-flex w-16 h-16 bg-[#8A151B] items-center justify-center mb-6">
                    <Send size={24} className="text-white" />
                  </div>
                  <h3 className="font-display text-3xl font-bold text-[#0A0A0A]">
                    Vielen Dank!
                  </h3>
                  <p className="mt-3 text-neutral-600 max-w-md mx-auto">
                    Ihre Nachricht ist angekommen. Ich melde mich zeitnah
                    persönlich bei Ihnen zurück.
                  </p>
                  <button
                    onClick={() => setDone(false)}
                    data-testid="contact-new-message-button"
                    className="mt-8 text-sm uppercase tracking-wider font-semibold text-[#8A151B] hover:text-[#6B1015] transition-colors"
                  >
                    Weitere Nachricht senden →
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">
                        Name *
                      </label>
                      <input
                        data-testid="contact-input-name"
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        placeholder="Ihr Name"
                        className={inputCls}
                        required
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">
                        E-Mail *
                      </label>
                      <input
                        data-testid="contact-input-email"
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        placeholder="ihre@email.de"
                        className={inputCls}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">
                        Telefon
                      </label>
                      <input
                        data-testid="contact-input-phone"
                        type="tel"
                        value={form.telefon}
                        onChange={update("telefon")}
                        placeholder="Optional"
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">
                        Anliegen *
                      </label>
                      <select
                        data-testid="contact-select-anliegen"
                        value={form.anliegen}
                        onChange={update("anliegen")}
                        className={`${inputCls} cursor-pointer`}
                        required
                      >
                        <option value="">Bitte wählen</option>
                        {ANLIEGEN.map((a) => (
                          <option key={a.value} value={a.value}>
                            {a.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] font-bold text-neutral-500">
                      Nachricht *
                    </label>
                    <textarea
                      data-testid="contact-input-message"
                      value={form.nachricht}
                      onChange={update("nachricht")}
                      rows={5}
                      placeholder="Beschreiben Sie kurz, worum es geht…"
                      className={`${inputCls} resize-none`}
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="contact-submit-button"
                    disabled={submitting}
                    className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#8A151B] text-white hover:bg-[#6B1015] disabled:opacity-60 disabled:cursor-not-allowed px-10 py-4 font-semibold tracking-wide transition-all duration-300 uppercase text-sm"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={18} className="animate-spin" />
                        Wird gesendet…
                      </>
                    ) : (
                      <>
                        Nachricht senden
                        <Send
                          size={16}
                          className="group-hover:translate-x-1 transition-transform"
                        />
                      </>
                    )}
                  </button>

                  <p className="text-xs text-neutral-500 leading-relaxed">
                    Mit dem Absenden stimmen Sie zu, dass Ihre Angaben zur
                    Bearbeitung der Anfrage gespeichert werden. Es erfolgt
                    keine Weitergabe an Dritte.
                  </p>
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
